import {
    QueryClient,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import { DateString } from 'routes/HumanResourcesModule/business/types';
import {
    HttpContentWithData,
    HttpError,
    HttpResponse,
    RQAPICallWithData,
} from 'store/APIClient';
import { SIMULATION_ENDPOINT_PREFIX } from 'routes/HumanResourcesModule/Simulation/helpers/constants';
import { dispatch } from 'store/store';
import {
    camelizeKeys,
    processTopLevelKeys,
    snakify,
} from 'helpers/syntaxHelper';
import { EnvelopeHypothesisMutationParams } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/helpers/types';
import { useEnvelopeMantyStatusHypothesesChangesContext } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/MantyStatusHypothesis/contexts/EnvelopeMantyStatusHypothesesChangesContext';
import { transformDateKeys } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/components/ValueByDate/helpers/apiHelpers';
import { getChangedValuesByDates } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/components/ValueByDate/helpers/trackingHelpers';
import {
    invalidateDetailedEnvelopeMantyStatusHypothesesQueries,
    resetResultSimulationDataQueries,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Result/helpers/hooks/resultSimulationData';
import { TRACKABLE_EVENTS, trackEventPRH } from 'helpers/trackerHelpers';
import { useRef } from 'react';
import _ from 'lodash';
import { dateStringToMonth } from 'routes/HumanResourcesModule/business/helpers';
import {
    DetailedEnvelopeMantyStatusHypothesis,
    EnvelopeMantyStatusHypothesis,
} from '../types';

const ENVELOPE_MANTY_STATUS_HYPOTHESES_API_KEY =
    'envelope_manty_status_hypotheses';
export const DETAILED_ENVELOPE_MANTY_STATUS_HYPOTHESES_API_KEY =
    'detailed_envelope_manty_status_hypotheses';

type EnvelopeMantyStatusHypothesisMutationParams = {
    comment: string;
    valuesByDates: Record<DateString, number>;
};

const useEnvelopeMantyStatusHypothesisPostMutation = (
    onSuccess: () => void,
    simulationId: number,
    orgChartHrSoftwareId?: string,
    mantyStatus?: string,
) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            comment,
            valuesByDates,
        }: EnvelopeMantyStatusHypothesisMutationParams) => {
            if (!orgChartHrSoftwareId || !mantyStatus) {
                return {} as Promise<
                    HttpResponse<
                        HttpContentWithData<EnvelopeMantyStatusHypothesis>
                    >
                >;
            }
            return RQAPICallWithData<EnvelopeMantyStatusHypothesis>({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/${simulationId}/envelope_manty_status_hypotheses`,
                method: 'POST',
                body: {
                    orgChartHrSoftwareId,
                    mantyStatus,
                    comment,
                    newValuesByDates: transformDateKeys(valuesByDates),
                },
                preProcessingBodyCallback: (body) =>
                    processTopLevelKeys(snakify, body),
                skipCaseConversion: true,
            });
        },

        onSuccess: ({ content }) => {
            onSuccess();
            const camelizedData = camelizeKeys(
                content.data,
            ) as unknown as EnvelopeMantyStatusHypothesis;
            const existingData = queryClient.getQueryData<
                EnvelopeMantyStatusHypothesis[]
            >([ENVELOPE_MANTY_STATUS_HYPOTHESES_API_KEY, simulationId]);

            if (existingData) {
                existingData.push(camelizedData);

                queryClient.setQueryData(
                    [ENVELOPE_MANTY_STATUS_HYPOTHESES_API_KEY, simulationId],
                    existingData,
                );
            }

            dispatch.vision.notifyUser({
                message: content.message,
                snackType: 'success',
            });

            trackEventPRH(
                TRACKABLE_EVENTS.human_resources.simulation.nonRecurrent
                    .validateEnvelopeHypothesis,
                {
                    ..._.mapKeys(
                        camelizedData.newValuesByDates,
                        (v, k: DateString) => dateStringToMonth(k),
                    ),
                    mantyStatus,
                },
            );
        },
        meta: { useGlobalOnError: true },
    });
};

const useEnvelopeMantyStatusHypothesisEditMutation = (
    onSuccess: () => void,
    simulationId: number,
    hypothesisId?: number,
) => {
    const queryClient = useQueryClient();

    // We are using a ref to keep track of the oldValuesByDates passed to the mutate function
    // and use it later in the onSuccess callback.
    const oldValuesByDatesRef = useRef<Record<DateString, number>>({});

    return useMutation({
        mutationFn: async ({
            comment,
            valuesByDates,
            oldValuesByDates,
        }: EnvelopeHypothesisMutationParams) => {
            if (hypothesisId === undefined) {
                return { content: { message: '', data: undefined } };
            }
            if (oldValuesByDates) {
                oldValuesByDatesRef.current = oldValuesByDates;
            }
            return RQAPICallWithData<
                SnakifyKeys<EnvelopeMantyStatusHypothesis>
            >({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/envelope_manty_status_hypothesis/${hypothesisId}`,
                method: 'PUT',
                body: {
                    comment,
                    newValuesByDates: transformDateKeys(valuesByDates),
                },
                preProcessingBodyCallback: (body) =>
                    processTopLevelKeys(snakify, body),
                skipCaseConversion: true,
            });
        },

        onSuccess: ({ content }) => {
            onSuccess();
            const camelizedData = content.data
                ? (camelizeKeys(content.data) as EnvelopeMantyStatusHypothesis)
                : ({} as EnvelopeMantyStatusHypothesis);
            const existingData = queryClient.getQueryData<
                EnvelopeMantyStatusHypothesis[]
            >([ENVELOPE_MANTY_STATUS_HYPOTHESES_API_KEY, simulationId]);

            if (existingData && hypothesisId !== undefined) {
                const updatedData = existingData.map((hypothesis) =>
                    hypothesis.id === hypothesisId ? camelizedData : hypothesis,
                );
                queryClient.setQueryData(
                    [ENVELOPE_MANTY_STATUS_HYPOTHESES_API_KEY, simulationId],
                    updatedData,
                );
            }
            dispatch.vision.notifyUser({
                message: content.message ?? '',
                snackType: 'success',
            });

            const changedValuesByDates = getChangedValuesByDates(
                camelizedData.newValuesByDates ?? {},
                oldValuesByDatesRef.current,
            );

            trackEventPRH(
                TRACKABLE_EVENTS.human_resources.simulation.nonRecurrent
                    .validateEnvelopeHypothesis,
                changedValuesByDates,
            );
            oldValuesByDatesRef.current = {};
        },

        onError: (error: HttpError) => {
            dispatch.vision.notifyUser({
                message: error.message,
                snackType: 'error',
            });
            oldValuesByDatesRef.current = {};
        },
    });
};

const useEnvelopeMantyStatusHypothesisDeleteMutation = (
    onSuccess: () => void,
    simulationId: number,
    hypothesisId?: number,
) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            if (hypothesisId === undefined) return undefined;

            return RQAPICallWithData({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/envelope_manty_status_hypothesis/${hypothesisId}`,
                method: 'DELETE',
            });
        },

        onSuccess: (response) => {
            onSuccess();
            const existingData = queryClient.getQueryData<
                EnvelopeMantyStatusHypothesis[]
            >([ENVELOPE_MANTY_STATUS_HYPOTHESES_API_KEY, simulationId]);

            if (existingData && hypothesisId !== undefined) {
                const updatedData = existingData.filter(
                    (hypothesis) => hypothesis.id !== hypothesisId,
                );
                queryClient.setQueryData(
                    [ENVELOPE_MANTY_STATUS_HYPOTHESES_API_KEY, simulationId],
                    updatedData,
                );
            }

            dispatch.vision.notifyUser({
                message: response?.content.message ?? '',
                snackType: 'success',
            });
        },
        meta: { useGlobalOnError: true },
    });
};

export const useEnvelopeMantyStatusHypothesisCRUDMutations = (
    simulationId: number,
    orgChartHrSoftwareId?: string,
    mantyStatus?: string,
    hypothesisId?: number,
) => {
    const { setHasChanges } = useEnvelopeMantyStatusHypothesesChangesContext();
    const queryClient = useQueryClient();
    const onSuccess = () => {
        invalidateDetailedEnvelopeMantyStatusHypothesesQueries(
            queryClient,
            simulationId,
        );
        resetResultSimulationDataQueries(queryClient, simulationId);
        setHasChanges(true);
    };

    const { mutate: createHypothesis, isPending: createLoading } =
        useEnvelopeMantyStatusHypothesisPostMutation(
            onSuccess,
            simulationId,
            orgChartHrSoftwareId,
            mantyStatus,
        );
    const { mutate: editHypothesis, isPending: editLoading } =
        useEnvelopeMantyStatusHypothesisEditMutation(
            onSuccess,
            simulationId,
            hypothesisId,
        );
    const { mutate: deleteHypothesis, isPending: deleteLoading } =
        useEnvelopeMantyStatusHypothesisDeleteMutation(
            onSuccess,
            simulationId,
            hypothesisId,
        );

    return {
        createHypothesis,
        editHypothesis,
        deleteHypothesis,
        isLoading: createLoading || editLoading || deleteLoading,
    };
};

export const resetEnvelopeMantyStatusHypothesesQueries = (
    hrSimulationId: number,
    queryClient: QueryClient,
) => {
    queryClient.resetQueries({
        queryKey: [ENVELOPE_MANTY_STATUS_HYPOTHESES_API_KEY, hrSimulationId],
    });
};

export const useGetEnvelopeMantyStatusHypotheses = (simulationId: number) =>
    useQuery({
        queryKey: [ENVELOPE_MANTY_STATUS_HYPOTHESES_API_KEY, simulationId],

        queryFn: async () => {
            const response = await RQAPICallWithData<
                EnvelopeMantyStatusHypothesis[]
            >({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/${simulationId}/envelope_manty_status_hypotheses`,
            });
            return response.content.data;
        },
    });

export const useGetDetailedEnvelopeMantyStatusHypotheses = (
    simulationId: number,
) =>
    useQuery({
        queryKey: [
            DETAILED_ENVELOPE_MANTY_STATUS_HYPOTHESES_API_KEY,
            simulationId,
        ],

        queryFn: async () => {
            const response = await RQAPICallWithData<
                DetailedEnvelopeMantyStatusHypothesis[]
            >({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/${simulationId}/detailed_envelope_manty_status_hypotheses`,
            });
            return response.content.data;
        },
    });
